import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/context/auth';
import { UserRole, hasAdminAccess } from '@/context/auth/types';
import { useRestaurantBranding } from '@/hooks/useRestaurantBranding';
import { 
  ComponentCategory, 
  SelectedComponents, 
  GlobalSettings, 
  GeneratedCode,
  DEFAULT_GLOBAL_SETTINGS 
} from '@/types/codeInjection';
import { 
  generateCode, 
  validateSelectedComponents, 
  getPreviewDescription,
  exportConfiguration,
  importConfiguration,
  validateSelectedComponentsEnhanced 
} from '@/utils/codeInjectionGenerator';
import { applyThemeToPreset } from '@/utils/themePresetHelper';
import { CodeInjectionPreset } from '@/types/codeInjection';
import ComponentSelector from './ComponentSelector';
import PresetSelector from './PresetSelector';
import CodeOutput from './CodeOutput';
import ColorPicker from './ColorPicker';
import AnimationSpeedSelector from './AnimationSpeedSelector';
import ThemeModeSelector from './ThemeModeSelector';
import { 
  Palette, 
  Code, 
  Settings, 
  Download, 
  Upload,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Layers,
  Building,
  Loader2
} from 'lucide-react';

/**
 * OrderingPageCustomizer - Main component for the Code Injection Creator
 * 
 * This component manages the state for component selection, global settings,
 * and code generation. It provides a two-panel interface with component 
 * selection on the left and code output on the right.
 */
const OrderingPageCustomizer: React.FC = () => {
  const { user } = useAuth();
  const userRole = user?.jwt_role || user?.role;
  const { getRestaurantColors, activeRestaurant } = useRestaurantBranding();

  // State management
  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>({});
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>(DEFAULT_GLOBAL_SETTINGS);
  const [generatedCode, setGeneratedCode] = useState<GeneratedCode>({
    headCode: '',
    bodyCode: '',
    stats: { headSize: 0, bodySize: 0, totalComponents: 0 }
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | undefined>();

  // Check if user has access to this feature
  const hasAccess = hasAdminAccess(userRole);

  // Memoized validation and preview
  const isValidSelection = useMemo(() => 
    validateSelectedComponents(selectedComponents), 
    [selectedComponents]
  );

  const enhancedValidation = useMemo(() => 
    validateSelectedComponentsEnhanced(selectedComponents), 
    [selectedComponents]
  );

  const previewDescription = useMemo(() => 
    getPreviewDescription(selectedComponents), 
    [selectedComponents]
  );

  // Update global settings when restaurant colors are loaded
  useEffect(() => {
    const restaurantColors = getRestaurantColors();
    // Only update if we have valid restaurant colors (not the fallback colors)
    if (activeRestaurant && restaurantColors.primaryColor !== '#c5b068') {
      setGlobalSettings(prev => {
        // Create new menu item text styles with the restaurant colors
        const newMenuItemTextRest = prev.menuItemTextRest ? {
          ...prev.menuItemTextRest,
          color: prev.menuItemTextRest.color === '#ffffff' ? '#ffffff' : restaurantColors.primaryColor
        } : {
          ...DEFAULT_GLOBAL_SETTINGS.menuItemTextRest!,
          color: '#ffffff'
        };

        const newMenuItemTextHover = prev.menuItemTextHover ? {
          ...prev.menuItemTextHover,
          color: restaurantColors.primaryColor
        } : {
          ...DEFAULT_GLOBAL_SETTINGS.menuItemTextHover!,
          color: restaurantColors.primaryColor
        };

        return {
          ...prev,
          ...restaurantColors,
          menuItemTextRest: newMenuItemTextRest,
          menuItemTextHover: newMenuItemTextHover,
          categoryHeaderGradientPrimary: restaurantColors.primaryColor,
          categoryHeaderGradientSecondary: restaurantColors.secondaryColor,
          navLogoGradientPrimary: restaurantColors.primaryColor,
          navLogoGradientSecondary: restaurantColors.secondaryColor,
          navLogoGlowColor: restaurantColors.primaryColor,
          categoryUnderlineColor: restaurantColors.primaryColor,
          categoryUnderlinePrimaryColor: restaurantColors.primaryColor,
          categoryUnderlineSecondaryColor: restaurantColors.secondaryColor,
          // Update price tag colors to use restaurant colors
          priceTagBackgroundColor: restaurantColors.primaryColor,
          priceTagGradientStartColor: restaurantColors.primaryColor,
          priceTagGradientEndColor: restaurantColors.secondaryColor,
          // Update welcome message gradient colors to use restaurant colors  
          welcomeMessageGradientStartColor: restaurantColors.primaryColor,
          welcomeMessageGradientEndColor: restaurantColors.secondaryColor,
          // Update button states to use restaurant colors
          buttonRestState: {
            ...prev.buttonRestState,
            backgroundColor: restaurantColors.primaryColor,
            borderColor: restaurantColors.primaryColor
          },
          buttonHoverState: {
            ...prev.buttonHoverState,
            backgroundColor: restaurantColors.secondaryColor,
            borderColor: restaurantColors.secondaryColor
          },
          // Update button shadow colors to use restaurant colors
          buttonShadowRest: {
            ...prev.buttonShadowRest,
            color: restaurantColors.primaryColor
          },
          buttonShadowHover: {
            ...prev.buttonShadowHover,
            color: restaurantColors.secondaryColor
          },
          // Update restaurant name from context if available
          restaurantName: activeRestaurant?.name || prev.restaurantName || 'our restaurant',
          // Update search button colors to use restaurant colors
          searchButtonRestState: {
            ...prev.searchButtonRestState,
            backgroundColor: restaurantColors.primaryColor
          },
          searchButtonHoverState: {
            ...prev.searchButtonHoverState,
            backgroundColor: restaurantColors.secondaryColor
          },
          // Update enhanced badge colors to use restaurant colors
          tagBadgeEnhancedPrimaryColor: restaurantColors.primaryColor,
          tagBadgeEnhancedSecondaryColor: restaurantColors.secondaryColor
        };
      });
    }
  }, [activeRestaurant, getRestaurantColors]);

  // Generate code whenever selections or settings change
  useEffect(() => {
    if (isValidSelection) {
      setIsGenerating(true);
      // Add small delay to show loading state
      const timer = setTimeout(() => {
        const newCode = generateCode(selectedComponents, globalSettings);
        setGeneratedCode(newCode);
        setIsGenerating(false);
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      setGeneratedCode({
        headCode: '',
        bodyCode: '',
        stats: { headSize: 0, bodySize: 0, totalComponents: 0 }
      });
    }
  }, [selectedComponents, globalSettings, isValidSelection]);

  // Handle component selection - supports both single and multiple selections
  const handleComponentChange = (category: ComponentCategory, componentId: string | string[] | null) => {
    setSelectedComponents(prev => ({
      ...prev,
      [category]: componentId
    }));
    
    // Clear preset selection when components are manually changed
    if (selectedPreset) {
      setSelectedPreset(undefined);
    }
  };

  // Handle global settings changes
  const handleGlobalSettingChange = (setting: keyof GlobalSettings, value: any) => {
    setGlobalSettings(prev => ({
      ...prev,
      [setting]: value
    }));
    
    // Clear preset selection when settings are manually changed
    if (selectedPreset) {
      setSelectedPreset(undefined);
    }
  };

  // Handle preset selection
  const handlePresetSelect = (preset: CodeInjectionPreset) => {
    // Apply theme adjustments to the preset before using it
    const currentColorMode = globalSettings.colorMode || 'dark';
    const themedPreset = applyThemeToPreset(preset, currentColorMode);
    
    setSelectedComponents(themedPreset.selectedComponents);
    
    // Preserve current restaurant colors when applying preset
    const currentRestaurantName = globalSettings.restaurantName || activeRestaurant?.name || 'our restaurant';
    
    setGlobalSettings(prev => ({
      ...themedPreset.globalSettings,
      // Preserve color mode selection
      colorMode: prev.colorMode || 'dark',
      // Preserve restaurant brand colors from current settings
      primaryColor: prev.primaryColor,
      secondaryColor: prev.secondaryColor,
      // Preserve any color-related settings that should use restaurant colors
      categoryHeaderGradientPrimary: prev.primaryColor,
      categoryHeaderGradientSecondary: prev.secondaryColor,
      navLogoGradientPrimary: prev.primaryColor,
      navLogoGradientSecondary: prev.secondaryColor,
      navLogoGlowColor: prev.primaryColor,
      categoryUnderlineColor: prev.primaryColor,
      categoryUnderlinePrimaryColor: prev.primaryColor,
      categoryUnderlineSecondaryColor: prev.secondaryColor,
      priceTagBackgroundColor: prev.primaryColor,
      priceTagGradientStartColor: prev.primaryColor,
      priceTagGradientEndColor: prev.secondaryColor,
      welcomeMessageGradientStartColor: prev.primaryColor,
      welcomeMessageGradientEndColor: prev.secondaryColor,
      // Preserve enhanced tag badge colors using restaurant colors
      tagBadgeEnhancedPrimaryColor: prev.primaryColor,
      tagBadgeEnhancedSecondaryColor: prev.secondaryColor,
      // Preserve special badge colors from current settings
      tagBadgeVeganPrimaryColor: prev.tagBadgeVeganPrimaryColor,
      tagBadgeVeganSecondaryColor: prev.tagBadgeVeganSecondaryColor,
      tagBadgeVegetarianPrimaryColor: prev.tagBadgeVegetarianPrimaryColor,
      tagBadgeVegetarianSecondaryColor: prev.tagBadgeVegetarianSecondaryColor,
      tagBadgeGlutenFreePrimaryColor: prev.tagBadgeGlutenFreePrimaryColor,
      tagBadgeGlutenFreeSecondaryColor: prev.tagBadgeGlutenFreeSecondaryColor,
      tagBadgeSpicyPrimaryColor: prev.tagBadgeSpicyPrimaryColor,
      tagBadgeSpicySecondaryColor: prev.tagBadgeSpicySecondaryColor,
      tagBadgeDairyFreePrimaryColor: prev.tagBadgeDairyFreePrimaryColor,
      tagBadgeDairyFreeSecondaryColor: prev.tagBadgeDairyFreeSecondaryColor,
      tagBadgeNutFreePrimaryColor: prev.tagBadgeNutFreePrimaryColor,
      tagBadgeNutFreeSecondaryColor: prev.tagBadgeNutFreeSecondaryColor,
      tagBadgeHalalPrimaryColor: prev.tagBadgeHalalPrimaryColor,
      tagBadgeHalalSecondaryColor: prev.tagBadgeHalalSecondaryColor,
      tagBadgePopularPrimaryColor: prev.tagBadgePopularPrimaryColor,
      tagBadgePopularSecondaryColor: prev.tagBadgePopularSecondaryColor,
      tagBadgeNewPrimaryColor: prev.tagBadgeNewPrimaryColor,
      tagBadgeNewSecondaryColor: prev.tagBadgeNewSecondaryColor,
      tagBadgeDealPrimaryColor: prev.tagBadgeDealPrimaryColor,
      tagBadgeDealSecondaryColor: prev.tagBadgeDealSecondaryColor,
      buttonRestState: {
        ...prev.buttonRestState,
        backgroundColor: prev.primaryColor,
        borderColor: prev.primaryColor
      },
      buttonHoverState: {
        ...prev.buttonHoverState,
        backgroundColor: prev.secondaryColor,
        borderColor: prev.secondaryColor
      },
      // Preserve button shadow colors with restaurant colors
      buttonShadowRest: {
        ...prev.buttonShadowRest,
        color: prev.primaryColor
      },
      buttonShadowHover: themedPreset.globalSettings.buttonShadowHover ? {
        ...themedPreset.globalSettings.buttonShadowHover,
        color: themedPreset.globalSettings.buttonShadowHover.color === 'USE_PRIMARY_COLOR' ? prev.primaryColor : 
               themedPreset.globalSettings.buttonShadowHover.color !== '#000000' ? themedPreset.globalSettings.buttonShadowHover.color : prev.primaryColor
      } : {
        ...prev.buttonShadowHover,
        color: prev.secondaryColor
      },
      // Apply preset menu item text styles (already theme-adjusted)
      menuItemTextRest: themedPreset.globalSettings.menuItemTextRest || prev.menuItemTextRest,
      menuItemTextHover: themedPreset.globalSettings.menuItemTextHover ? {
        ...themedPreset.globalSettings.menuItemTextHover,
        color: prev.primaryColor
      } : prev.menuItemTextHover,
      // Preserve restaurant name
      restaurantName: currentRestaurantName,
      // Update search button colors to use restaurant colors
      searchButtonRestState: {
        ...prev.searchButtonRestState,
        backgroundColor: prev.primaryColor
      },
      searchButtonHoverState: {
        ...prev.searchButtonHoverState,
        backgroundColor: prev.secondaryColor
      }
    }));
    
    setSelectedPreset(preset.id);
  };

  // Handle preset clearing
  const handleClearPreset = () => {
    setSelectedPreset(undefined);
  };

  // Load restaurant colors
  const loadRestaurantColors = () => {
    setIsLoadingColors(true);
    try {
      const restaurantColors = getRestaurantColors();
      setGlobalSettings(prev => ({
        ...prev,
        ...restaurantColors
      }));
    } catch (error) {
      console.error('Failed to load restaurant colors:', error);
    } finally {
      setIsLoadingColors(false);
    }
  };

  // Reset all selections
  const handleReset = () => {
    setSelectedComponents({});
    // Keep the current primary/secondary colors but reset everything else
    const resetSettings = {
      ...DEFAULT_GLOBAL_SETTINGS,
      primaryColor: globalSettings.primaryColor,
      secondaryColor: globalSettings.secondaryColor,
      menuItemTextRest: {
        ...DEFAULT_GLOBAL_SETTINGS.menuItemTextRest!,
        color: '#ffffff'
      },
      menuItemTextHover: {
        ...DEFAULT_GLOBAL_SETTINGS.menuItemTextHover!,
        color: globalSettings.primaryColor
      },
      categoryHeaderGradientPrimary: globalSettings.primaryColor,
      categoryHeaderGradientSecondary: globalSettings.secondaryColor,
      navLogoGradientPrimary: globalSettings.primaryColor,
      navLogoGradientSecondary: globalSettings.secondaryColor,
      navLogoGlowColor: globalSettings.primaryColor,
      categoryUnderlineColor: globalSettings.primaryColor,
      categoryUnderlinePrimaryColor: globalSettings.primaryColor,
      categoryUnderlineSecondaryColor: globalSettings.secondaryColor,
      searchButtonRestState: {
        backgroundColor: globalSettings.primaryColor,
        svgColor: '#ffffff'
      },
      searchButtonHoverState: {
        backgroundColor: globalSettings.secondaryColor,
        svgColor: '#ffffff'
      }
    };
    setGlobalSettings(resetSettings);
  };

  // Export configuration
  const handleExport = () => {
    const config = exportConfiguration(selectedComponents, globalSettings);
    const blob = new Blob([config], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ordering-page-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import configuration
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        const config = importConfiguration(result);
        if (config) {
          setSelectedComponents(config.selectedComponents);
          setGlobalSettings(config.globalSettings);
        }
      }
    };
    reader.readAsText(file);
  };

  // Show access denied message if user doesn't have permission
  if (!hasAccess) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            You need restaurant admin access or higher to use the Ordering Page customiser.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-screen">
      {/* Left Panel - Tabbed Interface for Settings and Components */}
      <div className="space-y-6 overflow-y-auto scrollbar-hide">
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="settings" className="flex items-center gap-1 data-[state=active]:text-white group text-xs">
              <Settings className="h-3 w-3 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="presets" className="flex items-center gap-1 data-[state=active]:text-white group text-xs">
              <Building className="h-3 w-3 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              Presets
            </TabsTrigger>
            <TabsTrigger value="components" className="flex items-center gap-1 data-[state=active]:text-white group text-xs">
              <Layers className="h-3 w-3 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              Components
            </TabsTrigger>
          </TabsList>

          {/* Global Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Global Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <ColorPicker
                  label="Primary Colour"
                  value={globalSettings.primaryColor}
                  onChange={(color) => handleGlobalSettingChange('primaryColor', color)}
                />
                
                <ColorPicker
                  label="Secondary Colour"
                  value={globalSettings.secondaryColor}
                  onChange={(color) => handleGlobalSettingChange('secondaryColor', color)}
                />
                
                {/* Load Restaurant Colors Button */}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadRestaurantColors}
                    disabled={!activeRestaurant || isLoadingColors}
                    className="flex items-center gap-2"
                  >
                    {isLoadingColors ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Palette className="h-4 w-4" />
                    )}
                    Load Restaurant Colours
                  </Button>
                </div>
                
                <AnimationSpeedSelector
                  value={globalSettings.animationSpeed}
                  onChange={(speed) => handleGlobalSettingChange('animationSpeed', speed)}
                />
                
                <ThemeModeSelector
                  value={globalSettings.colorMode || 'dark'}
                  onChange={(mode) => handleGlobalSettingChange('colorMode', mode)}
                />
              </CardContent>
            </Card>

            {/* Configuration Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Current Selection Preview */}
                  <div className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-2 mb-2">
                      {enhancedValidation.isValid ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium text-green-700">
                            Ready to Generate
                          </span>
                        </>
                      ) : enhancedValidation.errors.length > 0 ? (
                        <>
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm font-medium text-red-700">
                            {enhancedValidation.errors.length} Error{enhancedValidation.errors.length > 1 ? 's' : ''}
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-500">
                            Select Components
                          </span>
                        </>
                      )}
                      {enhancedValidation.warnings.length > 0 && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm font-medium text-yellow-600">
                            {enhancedValidation.warnings.length} Warning{enhancedValidation.warnings.length > 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {previewDescription}
                    </p>
                    {enhancedValidation.errors.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs text-red-600">
                          {enhancedValidation.errors[0]}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      className="flex items-center gap-1"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reset
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={handleExport}
                      disabled={!isValidSelection}
                      className="flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </Button>
                    
                    <label className="cursor-pointer">
                      <Button
                        variant="outline"
                        asChild
                        className="flex items-center gap-1"
                      >
                        <div>
                          <Upload className="h-4 w-4" />
                          Import
                        </div>
                      </Button>
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImport}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Presets Tab */}
          <TabsContent value="presets" className="mt-6">
            <PresetSelector
              selectedPreset={selectedPreset}
              onPresetSelect={handlePresetSelect}
              onClearPreset={handleClearPreset}
              currentComponents={selectedComponents}
              currentSettings={globalSettings}
            />
          </TabsContent>

          {/* Component Selection Tab */}
          <TabsContent value="components" className="mt-6">
            <ComponentSelector
              selectedComponents={selectedComponents}
              globalSettings={globalSettings}
              onComponentChange={handleComponentChange}
              onGlobalSettingChange={handleGlobalSettingChange}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Right Panel - Code Output */}
      <div className="space-y-6 overflow-y-auto pr-2">
        <CodeOutput
          generatedCode={generatedCode}
          isGenerating={isGenerating}
        />
      </div>
    </div>
  );
};

export default OrderingPageCustomizer;